import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Copy, Check, ClipboardPaste } from "lucide-react";

interface SelectionToolbarProps {
  onClipToSidebar: (content: string) => void;
}

export function SelectionToolbar({
  onClipToSidebar,
}: SelectionToolbarProps) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState("");
  const [selectedHtml, setSelectedHtml] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let hideTimeout: ReturnType<typeof setTimeout>;

    const handleMouseUp = () => {
      // Small delay to let the selection finish
      setTimeout(() => {
        const selection = window.getSelection();
        const text = selection?.toString().trim();

        if (!text || text.length === 0) {
          setShow(false);
          return;
        }

        // Check if selection is within a message
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          const element = container.nodeType === Node.ELEMENT_NODE 
            ? container as Element 
            : container.parentElement;

          if (!element?.closest('[data-message-content]')) {
            setShow(false);
            return;
          }

          // Get HTML content
          const div = document.createElement('div');
          div.appendChild(range.cloneContents());
          const html = div.innerHTML;

          // Get position
          const rect = range.getBoundingClientRect();
          const toolbarWidth = 200;
          const toolbarHeight = 45;
          
          let x = rect.left + rect.width / 2 - toolbarWidth / 2;
          let y = rect.top - toolbarHeight - 8;

          // Keep within viewport
          x = Math.max(8, Math.min(x, window.innerWidth - toolbarWidth - 8));
          
          // If too close to top, show below
          if (y < 8) {
            y = rect.bottom + 8;
          }

          setPosition({ x, y });
          setSelectedText(text);
          setSelectedHtml(html);
          setShow(true);
          setCopied(false);
        }
      }, 10);
    };

    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      // Don't hide if clicking the toolbar
      if (!target.closest('.selection-floating-toolbar')) {
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => {
          setShow(false);
        }, 100);
      }
    };

    const handleScroll = () => {
      setShow(false);
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('scroll', handleScroll, true);
      clearTimeout(hideTimeout);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(selectedText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleClip = () => {
    onClipToSidebar(selectedHtml || selectedText);
    setShow(false);
  };

  if (!show) return null;

  return createPortal(
    <div
      className="selection-floating-toolbar fixed z-[99999] flex items-center gap-1 px-2 py-1.5 bg-gray-900 rounded-lg shadow-2xl border border-gray-700"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded transition-colors text-sm font-medium"
        title="Copy to clipboard"
      >
        {copied ? (
          <>
            <Check className="h-3.5 w-3.5 text-green-400" />
            <span>Copied!</span>
          </>
        ) : (
          <>
            <Copy className="h-3.5 w-3.5" />
            <span>Copy</span>
          </>
        )}
      </button>

      <div className="w-px h-5 bg-white/20" />

      <button
        onClick={handleClip}
        className="flex items-center gap-1.5 px-3 py-1.5 text-white hover:bg-white/10 rounded transition-colors text-sm font-medium"
        title="Clip to sidebar"
      >
        <ClipboardPaste className="h-3.5 w-3.5" />
        <span>Clip</span>
      </button>
    </div>,
    document.body
  );
}
